import { DateTimeStr } from '@mi-front/core';
import { Box } from '@mui/material';
import { useTranscriptionBlocksFollowScroll } from '../hooks/useTranscriptionBlocksFollowScroll';
import {
  TranscriptionBlockData,
  TranscriptionSegmentData,
  TranscriptionWordData,
} from '../lib/transcription.types';
import { TranscriptionBlockItem } from './TranscriptionBlockItem';

type TranscriptionBlocksListProps = {
  blocks: TranscriptionBlockData[];
  activeBlockId: string | null;
  followPlayback: boolean;
  showWordEfficiency: boolean;
  /** Word ids that match the transcript search query. */
  matchWordIds: Set<string>;
  onUserInterruptedScroll: () => void;
  seekToTimeSeconds: (timeSeconds: number, chunkTime: DateTimeStr) => void;
  onWordClick: (timeSeconds: number, chunkTime: DateTimeStr) => void;
  isBlockActive: (segment: TranscriptionSegmentData) => boolean;
  isWordHighlighted: (word: TranscriptionWordData, blockActive: boolean) => boolean;
};

/**
 * Scrollable list of transcript blocks; optionally auto-scrolls the active block while follow mode is on.
 */
export function TranscriptionBlocksList({
  blocks,
  activeBlockId,
  followPlayback,
  showWordEfficiency,
  matchWordIds,
  onUserInterruptedScroll,
  seekToTimeSeconds,
  onWordClick,
  isBlockActive,
  isWordHighlighted,
}: TranscriptionBlocksListProps) {
  const { scrollContainerRef, activeBlockRefCallback, handleContainerScroll } =
    useTranscriptionBlocksFollowScroll({
      followPlayback,
      activeBlockId,
      onUserInterruptedScroll,
    });

  return (
    <Box
      ref={scrollContainerRef}
      onScroll={handleContainerScroll}
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
      }}
    >
      {blocks.map((block) => {
        const isScrollTarget = block.id === activeBlockId;

        return (
          <TranscriptionBlockItem
            key={block.id}
            ref={isScrollTarget ? activeBlockRefCallback : undefined}
            block={block}
            blockActive={isBlockActive(block.segment)}
            showWordEfficiency={showWordEfficiency}
            matchWordIds={matchWordIds}
            onSeekSegment={() => seekToTimeSeconds(block.segment.start, block.chunkTime)}
            onWordClick={(startSec) => onWordClick(startSec, block.chunkTime)}
            isWordHighlighted={isWordHighlighted}
          />
        );
      })}
    </Box>
  );
}
